import { useEffect, useReducer, useRef, useState, useSyncExternalStore } from "react";
import type { Execution, ModelInfo } from "../shared/types";

export class ApiError extends Error {
  constructor(message: string, public status: number, public detail?: any) {
    super(message);
  }
}

// ---------- acesso ----------
// Dois modos, decididos pelo servidor (GET /api/me):
//   local: a interface abre a sessão sozinha (cookie HttpOnly) e de novo se o servidor responder 401;
//   login: a sessão nasce de e-mail e senha; um 401 leva de volta à tela de entrada.
export interface Me {
  id: string;
  email: string;
  name: string;
}
type Auth = { status: "checking" | "local" | "in" | "login"; user: Me | null };
let auth: Auth = { status: "checking", user: null };
const authSubs = new Set<() => void>();
function setAuth(next: Auth) {
  auth = next;
  authSubs.forEach((f) => f());
}
export const useAuth = () => useSyncExternalStore((f) => (authSubs.add(f), () => void authSubs.delete(f)), () => auth);

// Chamada sem as regras de sessão (entrada, convite, /api/me).
export async function rawRequest(method: string, path: string, body?: any): Promise<any> {
  const res = await fetch(path, { method, headers: body !== undefined ? { "content-type": "application/json" } : {}, body: body !== undefined ? JSON.stringify(body) : undefined });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(data?.error ?? res.statusText, res.status, data?.detail);
  return data;
}

let authReady: Promise<void> | null = null;
export function checkAuth() {
  authReady ??= rawRequest("GET", "/api/me").then(
    (me) => setAuth(me.mode === "local" ? { status: "local", user: null } : { status: "in", user: me.user }),
    (e: ApiError) => {
      if (e.detail?.login) setAuth({ status: "login", user: null });
      else setAuth({ status: "local", user: null });
    }
  );
  return authReady;
}

export function loggedIn(user: Me) {
  session = null;
  setAuth({ status: "in", user });
  invalidate("");
}

export async function logoutNow() {
  await rawRequest("POST", "/api/logout").catch(() => {});
  cache.clear();
  setAuth({ status: "login", user: null });
}

let session: Promise<void> | null = null;
function ensureSession(renew = false) {
  if (auth.status !== "local") return Promise.resolve();
  if (!session || renew)
    session = fetch("/api/session", { method: "POST" }).then(async (r) => {
      if (!r.ok) {
        session = null;
        const data = await r.json().catch(() => null);
        if (data?.detail?.login) setAuth({ status: "login", user: null });
        throw new ApiError("Não foi possível abrir a sessão com o Ateliê", r.status);
      }
    });
  return session;
}

async function request(method: string, path: string, body?: any, retry = true): Promise<any> {
  await checkAuth();
  await ensureSession();
  const res = await fetch(path, {
    method,
    headers: body !== undefined ? { "content-type": "application/json" } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401 && auth.status !== "local") {
    setAuth({ status: "login", user: null });
    throw new ApiError("Sua sessão terminou. Entre de novo.", 401, { login: true });
  }
  if (res.status === 401 && retry) {
    await ensureSession(true);
    return request(method, path, body, false);
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(data?.error ?? res.statusText, res.status, data?.detail);
  return data;
}

export const api = {
  get: <T = any>(p: string): Promise<T> => request("GET", p),
  post: <T = any>(p: string, b: any = {}): Promise<T> => request("POST", p, b),
  put: <T = any>(p: string, b: any = {}): Promise<T> => request("PUT", p, b),
  patch: <T = any>(p: string, b: any = {}): Promise<T> => request("PATCH", p, b),
  del: <T = any>(p: string): Promise<T> => request("DELETE", p),
};

// Stream SSE via fetch (POST)
export async function streamPost(path: string, body: any, onEvent: (event: string, data: any) => void, signal?: AbortSignal) {
  await checkAuth();
  await ensureSession();
  const res = await fetch(path, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ ...body, stream: true }), signal });
  if (!res.ok || !res.body) {
    const data = await res.json().catch(() => null);
    throw new ApiError(data?.error ?? res.statusText, res.status, data?.detail);
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let i;
    while ((i = buf.indexOf("\n\n")) >= 0) {
      const chunk = buf.slice(0, i);
      buf = buf.slice(i + 2);
      let ev = "message";
      let data = "";
      for (const line of chunk.split("\n")) {
        if (line.startsWith("event: ")) ev = line.slice(7);
        else if (line.startsWith("data: ")) data += line.slice(6);
      }
      if (data) onEvent(ev, JSON.parse(data));
    }
  }
}

// ---------- cache de consultas ----------
type Sub = { load: () => void; render: () => void };
const cache = new Map<string, any>();
const subs = new Map<string, Set<Sub>>();
const inflight = new Map<string, Promise<any>>();

function fetchInto(path: string) {
  if (inflight.has(path)) return inflight.get(path)!;
  const p = api
    .get(path)
    .then((d) => {
      cache.set(path, d);
      subs.get(path)?.forEach((s) => s.render());
      return d;
    })
    .finally(() => inflight.delete(path));
  inflight.set(path, p);
  return p;
}

export function useQuery<T = any>(path: string | null, opts: { poll?: number } = {}) {
  const [, force] = useReducer((x) => x + 1, 0);
  const [error, setError] = useState<Error | null>(null);
  useEffect(() => {
    if (!path) return;
    let alive = true;
    const load = () => {
      fetchInto(path)
        .then(() => alive && setError(null))
        .catch((e) => alive && setError(e));
    };
    const sub: Sub = { load, render: () => alive && force() };
    const set = subs.get(path) ?? new Set();
    set.add(sub);
    subs.set(path, set);
    load();
    const iv = opts.poll ? setInterval(load, opts.poll) : null;
    return () => {
      alive = false;
      set.delete(sub);
      if (iv) clearInterval(iv);
    };
  }, [path, opts.poll]);
  return { data: (path ? cache.get(path) : undefined) as T | undefined, error, reload: () => path && fetchInto(path) };
}

export function mutateCache(path: string, data: any) {
  cache.set(path, data);
  subs.get(path)?.forEach((s) => s.render());
}

export function invalidate(prefix = "") {
  for (const [k, set] of subs) if (k.startsWith(prefix)) set.forEach((s) => s.load());
}

// ---------- status global ----------
export interface Status {
  home: string;
  auth: boolean;
  login: boolean;
  loginVia: "dock" | "senha" | null;
  dock: string | null;
  providers: Record<string, { ok: boolean; detail: string; lastError?: string; lastErrorAt?: number }>;
  models: ModelInfo[];
  seatbelt: boolean;
  agents: { enabled: boolean; reason: string | null };
  subscriptions: boolean;
  judgeModel: string;
}
export const useStatus = () => useQuery<Status>("/api/status", { poll: 30_000 }).data;

// ---------- toasts ----------
type Toast = { id: number; text: string; sub?: string; kind: "ok" | "error" };
let toasts: Toast[] = [];
const toastSubs = new Set<() => void>();
let tid = 0;
export function toast(text: string, sub?: string, kind: Toast["kind"] = "ok") {
  const t = { id: ++tid, text, sub, kind };
  toasts = [...toasts, t];
  toastSubs.forEach((f) => f());
  setTimeout(() => {
    toasts = toasts.filter((x) => x.id !== t.id);
    toastSubs.forEach((f) => f());
  }, kind === "error" ? 6000 : 3200);
}
export const toastError = (e: any) => toast(e?.message ?? String(e), undefined, "error");
export function useToasts() {
  return useSyncExternalStore(
    (f) => (toastSubs.add(f), () => toastSubs.delete(f)),
    () => toasts
  );
}

// ---------- formatação ----------
export function fmtMs(ms: number | null | undefined) {
  if (ms === null || ms === undefined) return "—";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(ms < 10_000 ? 2 : 1).replace(".", ",")} s`;
  return `${Math.floor(ms / 60_000)} min ${Math.round((ms % 60_000) / 1000)} s`;
}

export function fmtUsd(v: number | null | undefined, basis?: string) {
  if (basis === "free") return "grátis";
  if (v === null || v === undefined) return basis === "subscription" ? "assinatura" : "—";
  if (v === 0) return "$0";
  const s = v < 0.01 ? `$${v.toFixed(5)}` : v < 1 ? `$${v.toFixed(4)}` : `$${v.toFixed(2)}`;
  return basis === "subscription" ? `≈${s}` : s;
}

export function fmtTokens(n: number | null | undefined) {
  if (n === null || n === undefined) return "—";
  if (n < 1000) return String(n);
  return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0).replace(".", ",")}k`;
}

export function fmtBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1).replace(".", ",")} KB`;
  return `${(n / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
}

export function timeAgo(ts: number | null | undefined) {
  if (!ts) return "—";
  const s = Math.max(0, (Date.now() - ts) / 1000);
  if (s < 45) return "agora";
  if (s < 3600) return `há ${Math.round(s / 60)} min`;
  if (s < 86400) return `há ${Math.round(s / 3600)} h`;
  if (s < 86400 * 30) return `há ${Math.round(s / 86400)} d`;
  return new Date(ts).toLocaleDateString("pt-BR");
}

export function fmtDate(ts: number) {
  return new Date(ts).toLocaleString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0);

export function preview(v: any, n = 90) {
  const s = typeof v === "string" ? v : v === null || v === undefined ? "" : JSON.stringify(v);
  const one = (typeof v === "string" ? s.replace(/\*\*|`|^#+\s*/gm, "") : s).replace(/\s+/g, " ").trim();
  return one.length > n ? one.slice(0, n) + "…" : one;
}

export function refLabel(e: Pick<Execution, "version" | "channel">) {
  if (e.version === null) return "draft";
  return e.channel ? `v${e.version} · ${e.channel}` : `v${e.version}`;
}

export function useInterval(fn: () => void, ms: number | null) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (ms === null) return;
    const iv = setInterval(() => ref.current(), ms);
    return () => clearInterval(iv);
  }, [ms]);
}

export function useDebounced<T>(fn: (v: T) => void, ms: number) {
  const t = useRef<any>(null);
  const f = useRef(fn);
  f.current = fn;
  const call = (v: T) => {
    clearTimeout(t.current);
    t.current = setTimeout(() => f.current(v), ms);
  };
  call.flush = (v: T) => {
    clearTimeout(t.current);
    f.current(v);
  };
  return call;
}

export async function copy(text: string, what = "Copiado") {
  await navigator.clipboard.writeText(text);
  toast(what);
}

export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}
